import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { MenuItem, MenuDivider, Menu } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import classNames from 'classnames';
import styled from 'styled-components';
import * as R from 'ramda';
import intl from 'react-intl-universal';
import { CLASSES } from '@/constants/classes';

import { FormattedMessage as T } from '@/components/FormattedMessage';

import withDrawerActions from '@/containers/Drawer/withDrawerActions';

import { DRAWERS } from '@/constants/drawers';

// Item renderer.
const itemRenderer = (item: any, { modifiers, handleClick }: any) => (
  <MenuItem
    key={item.id}
    text={item.name}
    label={item.code}
    onClick={handleClick}
    active={modifiers.active}
  />
);

// Filters items - show all items when query is empty.
const filterItemsPredicater = (
  query: any,
  item: any,
  _index: any,
  exactMatch: any,
) => {
  // Show all items when query is empty
  if (!query) {
    return true;
  }

  const normalizedTitle = item.name.toLowerCase();
  const normalizedQuery = query.toLowerCase();

  if (exactMatch) {
    return normalizedTitle === normalizedQuery;
  } else {
    return `${normalizedTitle} ${item.code}`.indexOf(normalizedQuery) >= 0;
  }
};

// Handle input value renderer.
const handleInputValueRenderer = (inputValue: any) => {
  if (inputValue && inputValue.name) {
    return inputValue.name.toString();
  }
  return '';
};

function ItemsSuggestFieldRoot({
  items,
  initialItemId,
  selectedItemId,

  defautlSelectText = 'Select item',
  onItemSelected,

  sellable = false,
  purchasable = false,
  popoverFill = false,

  allowCreate = true,
  autofillRef,

  openDrawer,
  ...suggestProps
}: any) {
  // Filters items based on filter props.
  const filteredItems = useMemo(() => {
    let filteredItems = [...items];

    if (sellable) {
      filteredItems = filteredItems.filter((item: any) => item.sellable);
    }
    if (purchasable) {
      filteredItems = filteredItems.filter((item: any) => item.purchasable);
    }
    return filteredItems;
  }, [items, sellable, purchasable]);

  // Find initial item object.
  const initialItem = useMemo(
    () => filteredItems.some((a: any) => a.id === initialItemId),
    [initialItemId, filteredItems],
  );

  const [selectedItem, setSelectedItem] = useState(initialItem || null);
  const [isOpen, setIsOpen] = useState(false);

  const onItemSelect = useCallback(
    (item: any) => {
      if (item.id) {
        setSelectedItem({ ...item });
        onItemSelected && onItemSelected(item);
      } else {
        // Pass context about whether this item should be purchasable or sellable
        const drawerPayload = {
          autofillRef,
          itemContext: {
            purchasable,
            sellable,
          },
        };
        openDrawer(DRAWERS.QUICK_CREATE_ITEM, drawerPayload);
      }
      setIsOpen(false);
    },
    [
      setSelectedItem,
      onItemSelected,
      openDrawer,
      autofillRef,
      purchasable,
      sellable,
    ],
  );

  // Handle input click to open dropdown
  const handleInputClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (typeof selectedItemId !== 'undefined') {
      const item = selectedItemId
        ? filteredItems.find((a: any) => a.id === selectedItemId)
        : null;
      setSelectedItem(item);
    }
  }, [selectedItemId, filteredItems, setSelectedItem]);

  // Handle create new item click
  const handleCreateNewItem = useCallback(() => {
    const drawerPayload = {
      autofillRef,
      itemContext: {
        purchasable,
        sellable,
      },
    };
    openDrawer(DRAWERS.QUICK_CREATE_ITEM, drawerPayload);
    setIsOpen(false);
  }, [openDrawer, autofillRef, purchasable, sellable]);

  // Custom item list renderer with scrollable items and fixed "Create new" at bottom
  const itemListRenderer = useCallback(
    ({ items, itemsParentRef, query, renderItem, activeItem }: any) => {
      const renderedItems = items
        .map(renderItem)
        .filter((item: any) => item != null);
      const hasItems = renderedItems.length > 0;

      return (
        <ItemListContainer>
          <Menu ulRef={itemsParentRef} className="scrollable-items-menu">
            {hasItems ? (
              renderedItems
            ) : (
              <MenuItem disabled={true} text={<T id={'no_results'} />} />
            )}
          </Menu>
          {allowCreate && (
            <FixedCreateButton className="bp4-menu">
              <MenuItem
                icon="add"
                text={intl.get('create_new_item')}
                onClick={handleCreateNewItem}
              />
            </FixedCreateButton>
          )}
        </ItemListContainer>
      );
    },
    [allowCreate, handleCreateNewItem],
  );

  // Merge input props with onClick handler
  const mergedInputProps = {
    placeholder: defautlSelectText,
    onClick: handleInputClick,
    ...suggestProps.inputProps,
  };

  // Merge popover props with isOpen state
  const mergedPopoverProps = {
    minimal: true,
    boundary: 'window',
    isOpen: isOpen,
    popoverClassName: 'select-popover-in-drawer',
    onInteraction: (nextOpenState: any) => setIsOpen(nextOpenState),
    ...suggestProps.popoverProps,
  };

  // Get the default active item (first item or selected item)
  const defaultActiveItem =
    selectedItem || (filteredItems.length > 0 ? filteredItems[0] : null);

  return (
    <Suggest
      items={filteredItems}
      itemRenderer={itemRenderer}
      itemPredicate={filterItemsPredicater}
      inputValueRenderer={handleInputValueRenderer}
      selectedItem={selectedItem}
      activeItem={defaultActiveItem}
      onItemSelect={onItemSelect}
      inputProps={mergedInputProps}
      resetOnClose={true}
      fill={true}
      popoverProps={mergedPopoverProps}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      itemListRenderer={itemListRenderer}
      scrollToActiveItem={false}
      {...suggestProps}
    />
  );
}

const ItemListContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;

  .scrollable-items-menu {
    flex: 1;
    overflow-y: auto;
    max-height: 250px;
    margin: 0;
    padding: 5px 0;
  }
`;

const FixedCreateButton = styled.div`
  flex-shrink: 0;
  border-top: 1px solid rgba(16, 22, 26, 0.15);
  padding: 5px 0;

  .bp4-menu-item {
    margin: 0;
  }
`;

export const ItemsSuggestField = R.compose(withDrawerActions)(
  ItemsSuggestFieldRoot,
);
